import { createFileRoute } from '@tanstack/react-router';
import { Phone, Siren } from 'lucide-react';
import { useState } from 'react';

import { APP_PAGE_GUTTER_X_CLASSNAME } from '@apps/web/components/layout/page_layout';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { ClickToCallDialog } from '@apps/web/components/voice_calls/click_to_call_dialog';
import { EmergencyCallDialog } from '@apps/web/components/voice_calls/emergency_call_dialog';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { cn } from '@apps/web/lib/utils';
import {
    useOrganization,
    useOrganizationCaregivers,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/(main)/phone')({
    component: PhoneRoute,
    head: () => ({
        meta: [
            {
                title: 'Phone | Phoebe',
            },
        ],
    }),
});

type SelectedCaregiver = {
    readonly id: string;
    readonly name: string;
};

function PhoneRoute() {
    const { formatName } = useFormatPersonName();
    const organizationId = useOrganizationId();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 300);
    const [selectedCaregiver, setSelectedCaregiver] =
        useState<SelectedCaregiver | null>(null);
    const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
    const { data: organization, isLoading: isLoadingOrganization } =
        useOrganization(organizationId, {
            reason: 'phone-route',
        });
    const clickToCallEnabled = useFeatureFlag(
        'click_to_call_enabled',
        organization?.features,
    );
    const browserCallingEnabled = useFeatureFlag(
        'click_to_call_browser_enabled',
        organization?.features,
    );
    const emergencyCallingEnabled =
        organization?.features?.phone_system_e911_enabled === true &&
        browserCallingEnabled;
    const { data: caregivers, isLoading: isLoadingCaregivers } =
        useOrganizationCaregivers(
            clickToCallEnabled ? organizationId : null,
            { reason: 'phone-route' },
            { search: debouncedSearch, per_page: 25 },
        );

    if (!isLoadingOrganization && !clickToCallEnabled) {
        return (
            <main className="bg-background flex h-full min-h-0 flex-col">
                <div
                    className={cn(APP_PAGE_GUTTER_X_CLASSNAME, 'border-b py-4')}
                >
                    <h1 className="text-foreground text-xl font-semibold">
                        Phone
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Click-to-call is not enabled for this organization.
                    </p>
                </div>
            </main>
        );
    }

    return (
        <main className="bg-background flex h-full min-h-0 flex-col">
            <div
                className={cn(
                    APP_PAGE_GUTTER_X_CLASSNAME,
                    'flex flex-col items-start justify-between gap-3 border-b py-4 sm:flex-row sm:items-center sm:gap-4',
                )}
            >
                <div className="min-w-0">
                    <h1 className="text-foreground text-xl font-semibold">
                        Phone
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {browserCallingEnabled
                            ? 'Call a caregiver from your organization’s number, right from your browser.'
                            : 'Call a caregiver from your organization’s number. Phoebe calls your phone first, then connects you.'}
                    </p>
                </div>
                {emergencyCallingEnabled ? (
                    <Button
                        type="button"
                        variant="destructive"
                        onClick={() => setEmergencyDialogOpen(true)}
                    >
                        <Siren className="h-4 w-4" />
                        Emergency
                    </Button>
                ) : null}
            </div>
            <div
                className={cn(
                    APP_PAGE_GUTTER_X_CLASSNAME,
                    'flex-1 overflow-y-auto py-4',
                )}
            >
                <div className="max-w-xl space-y-4">
                    <Input
                        placeholder="Search caregivers by name"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                    {isLoadingCaregivers ? (
                        <div className="space-y-2">
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-12 w-full" />
                            <Skeleton className="h-12 w-full" />
                        </div>
                    ) : (
                        <ul className="divide-y rounded-md border">
                            {(caregivers ?? []).map((caregiver) => (
                                <li
                                    className="flex items-center justify-between gap-3 px-4 py-3"
                                    key={caregiver.id}
                                >
                                    <div className="min-w-0">
                                        <p className="truncate font-medium">
                                            {formatName(caregiver.name)}
                                        </p>
                                        {caregiver.office_name ? (
                                            <p className="text-muted-foreground truncate text-sm">
                                                {caregiver.office_name}
                                            </p>
                                        ) : null}
                                    </div>
                                    <Button
                                        className="shrink-0"
                                        onClick={() =>
                                            setSelectedCaregiver({
                                                id: caregiver.id,
                                                name: formatName(
                                                    caregiver.name,
                                                ),
                                            })
                                        }
                                        size="sm"
                                        type="button"
                                        variant="outline"
                                    >
                                        <Phone className="mr-2 h-4 w-4" />
                                        Call
                                    </Button>
                                </li>
                            ))}
                            {(caregivers ?? []).length === 0 ? (
                                <li className="text-muted-foreground px-4 py-6 text-center text-sm">
                                    No caregivers found.
                                </li>
                            ) : null}
                        </ul>
                    )}
                </div>
            </div>
            {selectedCaregiver && organizationId ? (
                <ClickToCallDialog
                    organizationId={organizationId}
                    caregiverId={selectedCaregiver.id}
                    caregiverName={formatName(selectedCaregiver.name)}
                    open={true}
                    onOpenChange={(open) => {
                        if (!open) {
                            setSelectedCaregiver(null);
                        }
                    }}
                />
            ) : null}
            {emergencyCallingEnabled && organizationId ? (
                <EmergencyCallDialog
                    organizationId={organizationId}
                    open={emergencyDialogOpen}
                    onOpenChange={setEmergencyDialogOpen}
                />
            ) : null}
        </main>
    );
}
